// useSoftphone.js
import { useState, useEffect, useRef } from 'react';
import JsSIP from 'jssip';

const useSoftphone = () => {
  const [sipUrl, setSipUrl] = useState(localStorage.getItem('sipUrl') || '');
  const [sipUser, setSipUser] = useState(localStorage.getItem('sipUser') || '');
  const [sipPass, setSipPass] = useState(localStorage.getItem('sipPass') || '');
  const [agentName, setAgentName] = useState(localStorage.getItem('agentName') || '');
  const [target, setTarget] = useState('');
  const [status, setStatus] = useState('Disconnected');
  const [registered, setRegistered] = useState(false);
  const [incoming, setIncoming] = useState(false);
  const [callState, setCallState] = useState('idle'); // idle, dialing, in_call, transferring
  const [agentState, setAgentState] = useState('logged_out');
  const [attendedSession, setAttendedSession] = useState(null);
  const [callerInfo, setCallerInfo] = useState('');
  const [outputDevices, setOutputDevices] = useState([]);
  const [selectedOutput, setSelectedOutput] = useState('');
  const [muted, setMuted] = useState(false);
  const [onHold, setOnHold] = useState(false);
  const [micVolume, setMicVolume] = useState(1);
  const [speakerVolume, setSpeakerVolume] = useState(1);
  const [networkStats, setNetworkStats] = useState({ jitter: 0, rtt: 0, loss: 0 });
  const [callHistory, setCallHistory] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('callHistory')) || [];
    } catch {
      return [];
    }
  });
  const addToHistory = (direction, number, duration = null) => {
    const entry = {
      direction,
      number,
      duration,
      timestamp: new Date().toLocaleString()
    };
    const updated = [entry, ...callHistory.slice(0, 19)];
    setCallHistory(updated);
    localStorage.setItem('callHistory', JSON.stringify(updated));
  };
  
  const [callActive, setCallActive] = useState(false);

  const uaRef = useRef(null);
  const sessionRef = useRef(null);
  const ringtoneRef = useRef(null);
  const peerRef = useRef(null);
  const socketRef = useRef(null);
  const emitStatus = async (statusValue) => {
    setAgentState(statusValue);
    if (socketRef.current?.readyState === 1) {
      socketRef.current.send(JSON.stringify({
        agent_id: sipUser,
        agent_name: agentName,
        status: statusValue,
        timestamp: Date.now()
      }));
    }

    if (statusValue === 'ready') {
      try {
        await fetch('https://softphone.thehotlyncapp.com:8443/agent/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ agent_id: sipUser })
        });
      } catch (e) {
        console.error('[Softphone] Failed to register agent:', e.message);
      }
    }
  };

  const emitEvent = (type, payload = {}) => {
    if (socketRef.current?.readyState === 1) {
      socketRef.current.send(JSON.stringify({
        type,
        agent_id: sipUser,
        agent_name: agentName,
        timestamp: Date.now(),
        ...payload
      }));
    }
  };

  const changeCallState = (next) => {
    setCallState(next);
    if (['dialing', 'in_call', 'transferring'].includes(next)) {
      emitStatus('busy');
    } else if (!['logged_out', 'on_break'].includes(agentState)) {
      emitStatus('ready');
    }
  };

  useEffect(() => {
    socketRef.current = new WebSocket('wss://softphone.thehotlyncapp.com:8443');
  
    socketRef.current.onopen = () => {
      if (registered) emitStatus(agentState);
    };
  
    socketRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
  
      if (data.type === 'call_answered' && (data.caller_id === sipUser || data.agent_id === sipUser)) {
        setCallActive(true);
        setStatus('In Call');
        setAgentState('busy');
      }
  
      if (data.type === 'call_ended' && (data.caller_id === sipUser || data.agent_id === sipUser)) {
        setCallActive(false);
        setStatus('Idle');
        setAgentState('ready');
      }
    };
  
    // Setup ringtone
    ringtoneRef.current = new Audio('/ringtone.mp3');
    ringtoneRef.current.loop = true;
  
    // Get output devices (speakers)
    navigator.mediaDevices.enumerateDevices().then(devices => {
      const outputs = devices.filter(d => d.kind === 'audiooutput');
      setOutputDevices(outputs);
      if (outputs[0]) setSelectedOutput(outputs[0].deviceId);
    });
  
    // Auto-register if credentials are present
    if (sipUrl && sipUser && sipPass) register();
  
    // Cleanup on browser/tab close
    window.addEventListener('beforeunload', () => {
      if (sessionRef.current && sessionRef.current.isEstablished()) {
        sessionRef.current.terminate();  // End SIP session
  
        emitEvent('call_ended', {
          remote: sessionRef.current.remote_identity.uri.user
        });
      }
  
      uaRef.current?.unregister();
      emitStatus('logged_out');
    });
  }, []);
  const register = () => {
    localStorage.setItem('sipUrl', sipUrl);
    localStorage.setItem('sipUser', sipUser);
    localStorage.setItem('sipPass', sipPass);
    localStorage.setItem('agentName', agentName);

    const socket = new JsSIP.WebSocketInterface(sipUrl);
    const configuration = {
      sockets: [socket],
      uri: `sip:${sipUser}@${new URL(sipUrl).hostname}`,
      password: sipPass,
      session_timers: false
    };

    const ua = new JsSIP.UA(configuration);
    uaRef.current = ua;

    ua.on('registered', () => {
      setStatus('Registered');
      setRegistered(true);
      emitStatus('ready');
    });

    ua.on('unregistered', () => {
      setStatus('Unregistered');
      setRegistered(false);
      emitStatus('logged_out');
    });

    ua.on('registrationFailed', () => {
      setStatus('Registration Failed');
      setRegistered(false);
    });

    ua.on('disconnected', () => {
      setStatus('Disconnected');
      setRegistered(false);
    });

    ua.on('newRTCSession', data => {
      const session = data.session;
      sessionRef.current = session;
      let acceptedLocally = false;

      if (data.originator === 'remote') {
        setAgentState('ringing');
        setCallerInfo(session.remote_identity.uri.toString());
        setIncoming(true);
        ringtoneRef.current.currentTime = 0;
        ringtoneRef.current.play().catch(() => {});
      }

      session.on('accepted', () => {
        acceptedLocally = true;
        setCallState('in_call');
        setIncoming(false);
        ringtoneRef.current.pause();
        monitorNetworkStats(session.connection);
        session.startTime = Date.now();
      
        setAgentState('busy'); // Update only after call is answered
        emitEvent('call_answered');
      });
      

      session.on('ended', () => {
        const duration = session.startTime ? Math.round((Date.now() - session.startTime) / 1000) : null;
        if (acceptedLocally) {
          addToHistory(data.originator === 'local' ? 'outgoing' : 'incoming', session.remote_identity.uri.user, duration);
        }
        emitEvent('call_ended', { duration, remote: session.remote_identity.uri.user });
        resetCallState();
      });

      session.on('failed', () => {
        emitEvent('call_ended', { remote: session.remote_identity.uri.user });
        resetCallState();
      });
    });

    ua.start();
    setStatus('Connecting...');
  };
  const call = async (overrideNumber = null) => {
    const number = overrideNumber || target;
    if (!number) return;
    setCallState('dialing'); // Only update local call state
    setAgentState('dialing'); // NEW: Reflect accurate status
  
    emitEvent('call_dialed', { to: number });
    const stream = await navigator.mediaDevices?.getUserMedia({ audio: true });

    uaRef.current.call(`sip:${number}@${new URL(sipUrl).hostname}`, {
      mediaConstraints: { audio: true, video: false },
      mediaStream: stream
    });
  };

  const answer = () => sessionRef.current?.answer({ mediaConstraints: { audio: true } });

  const hangup = () => {
    attendedSession?.terminate();
    sessionRef.current?.terminate();
    resetCallState();
  };

  const startAttendedTransfer = () => {
    sessionRef.current?.hold();
    const newSession = uaRef.current.call(`sip:${target}@${new URL(sipUrl).hostname}`, {
      mediaConstraints: { audio: true }
    });
    setAttendedSession(newSession);
    changeCallState('transferring');
  };

  const completeAttendedTransfer = () => {
    if (attendedSession && sessionRef.current) {
      sessionRef.current.refer(attendedSession.remote_identity.uri.toString());
      attendedSession.terminate();
      sessionRef.current.terminate();
    }
    resetCallState();
  };

  const cancelAttendedTransfer = () => {
    attendedSession?.terminate();
    sessionRef.current?.unhold();
    setAttendedSession(null);
    changeCallState('in_call');
  };

  const mute = () => {
    sessionRef.current?.mute({ audio: true });
    setMuted(true);
  };
  
  const unmute = () => {
    sessionRef.current?.unmute({ audio: true });
    setMuted(false);
  };
  
  const hold = () => {
    sessionRef.current?.hold();
    setOnHold(true);
  };
  
  const unhold = () => {
    sessionRef.current?.unhold();
    setOnHold(false);
  };
  const resetCallState = () => {
    setIncoming(false);
    setCallState('idle');
    setMuted(false);
    setOnHold(false);
    setAttendedSession(null);
    sessionRef.current = null;
    ringtoneRef.current.pause();
    peerRef.current = null;

    if (!['logged_out', 'on_break'].includes(agentState)) {
      emitStatus('ready');
    }
  };

  const monitorNetworkStats = (conn) => {
    peerRef.current = conn;
    const poll = async () => {
      if (!peerRef.current) return;
      const stats = await peerRef.current.getStats();
      stats.forEach(report => {
        if (report.type === 'remote-inbound-rtp' && report.kind === 'audio') {
          setNetworkStats(prev => ({
            ...prev,
            jitter: Math.round(report.jitter * 1000),
            loss: report.packetsLost || 0
          }));
        }
        if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          setNetworkStats(prev => ({
            ...prev,
            rtt: Math.round(report.currentRoundTripTime * 1000)
          }));
        }
      });
      setTimeout(poll, 3000);
    };
    poll();
  };

  return {
    sipUrl, setSipUrl, sipUser, setSipUser, sipPass, setSipPass, agentName, setAgentName,
    registered, register, call, answer, hangup, mute, unmute, hold, unhold,
    startAttendedTransfer, completeAttendedTransfer, cancelAttendedTransfer,
    target, setTarget, agentState, setAgentState, callState, setCallState,
    outputDevices, selectedOutput, setSelectedOutput, micVolume, setMicVolume,
    speakerVolume, setSpeakerVolume, networkStats, callerInfo, incoming, callActive,
    callHistory, emitStatus, muted, setMuted,
    onHold, setOnHold
  };
};

export default useSoftphone;
